import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
  DialogTrigger,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createUserSchema,
  CreateUserFormData,
  CreateFormFieldProps,
  SubmitAction,
} from '../types';
import { ROLES, UserRole, CreateUserAction } from '../constants';
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { useCreateUser } from '../hooks/usersApi';
import { ProviderTypesDropdown } from './ProviderTypesDropdown';
import { ProfileAccessDropdown } from './ProfileAccessDropdown';
import { Permission, useHasPermission } from '@/utils/roles';

export const CreateUserModal = () => {
  const [open, setOpen] = useState(false);
  const { mutate: createUser, isPending } = useCreateUser();
  const { hasPermission, currentUser } = useHasPermission([
    Permission.INVITE_USERS,
  ]);

  const defaultValues: CreateUserFormData = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    providerTypeId: '',
    role: currentUser?.role ?? UserRole.MANAGER,
    organizationProfileIds: [],
  };

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    mode: 'onSubmit',
    defaultValues,
  });

  const handleModalToggle = () => {
    form.reset(defaultValues);
    setOpen((open) => !open);
  };

  const handleSubmitClick = (action: SubmitAction) =>
    form.handleSubmit((data) => {
      const payload = {
        ...data,
        providerTypeId: data.providerTypeId || undefined,
      };
      createUser(payload, {
        onSuccess: () => {
          toast.success(`${data.firstName} ${data.lastName} was invited.`);
          if (action === CreateUserAction.CREATE_AND_ADD) {
            form.reset(defaultValues);
          } else {
            handleModalToggle();
          }
        },
        onError: (error) => {
          console.error(error);
          const errorMessage =
            error instanceof Error && error.message
              ? error.message
              : 'Failed to create user.';
          toast.error(errorMessage);
        },
      });
    });

  // hide create user button if user does not have permission
  if (!hasPermission) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleModalToggle}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="size-4" />
          New
        </Button>
      </DialogTrigger>
      <DialogContent className="mx-auto !max-w-[946px]">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-semibold">
            Create New User
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form>
            {/** input section */}
            <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }: CreateFormFieldProps<'firstName'>) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter first name"
                        aria-label="First Name"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }: CreateFormFieldProps<'lastName'>) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter last name"
                        aria-label="Last Name"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emailAddress"
                render={({ field }: CreateFormFieldProps<'emailAddress'>) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input
                        type="emailAddress"
                        {...field}
                        placeholder="Enter email address"
                        aria-label="Email Address"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="providerTypeId"
                render={({ field }: CreateFormFieldProps<'providerTypeId'>) => (
                  <FormItem>
                    <FormLabel>Provider Type</FormLabel>
                    <FormControl>
                      <ProviderTypesDropdown
                        enabled={open}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />
            </div>

            {/** role section */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }: CreateFormFieldProps<'role'>) => (
                <FormItem>
                  <FormLabel>Role *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      {ROLES.map(({ label, value, description }) => {
                        const isDisabled =
                          value === UserRole.OWNER &&
                          currentUser?.role !== UserRole.OWNER;
                        return (
                          <div
                            key={value}
                            className={`mb-4 flex items-start gap-2 ${isDisabled ? 'opacity-50' : ''}`}
                          >
                            <RadioGroupItem
                              value={value}
                              aria-label={label}
                              id={`radio-${value.toLowerCase().replace(/\s+/g, '-')}`}
                              className="mt-1"
                              disabled={isDisabled}
                            />
                            <label
                              htmlFor={`radio-${value.toLowerCase().replace(/\s+/g, '-')}`}
                              className={`flex flex-col ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <span className="text-foreground text-sm font-medium">
                                {label}
                              </span>
                              <span className="text-muted-foreground text-sm">
                                {description}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organizationProfileIds"
              render={({
                field,
              }: CreateFormFieldProps<'organizationProfileIds'>) => {
                const isOwner = form.watch('role') === UserRole.OWNER;

                return (
                  <FormItem className="mb-6 w-1/2">
                    <FormLabel>Profile Access *</FormLabel>
                    <FormControl>
                      <ProfileAccessDropdown
                        enabled={open}
                        values={field.value || []}
                        onChange={field.onChange}
                        disabled={isOwner}
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                );
              }}
            />

            <DialogFooter className="items-center border-t pt-4">
              {Object.keys(form.formState.errors).length > 0 && (
                <FormMessage className="text-destructive">
                  Please fix validation errors.
                </FormMessage>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleModalToggle}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="outline"
                aria-label="Create & Add Another"
                name="action"
                value={CreateUserAction.CREATE_AND_ADD}
                onClick={handleSubmitClick(CreateUserAction.CREATE_AND_ADD)}
                disabled={isPending}
                isLoading={isPending}
              >
                Send Invite & Add Another
              </Button>
              <Button
                type="submit"
                aria-label="Create"
                name="action"
                value={CreateUserAction.CREATE_AND_CLOSE}
                onClick={handleSubmitClick(CreateUserAction.CREATE_AND_CLOSE)}
                disabled={isPending}
                isLoading={isPending}
              >
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
